import React from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

const ROUTES = [
  { path: '/dashboard', label: 'Overview', desc: 'Pilot home, quick actions, and module entry points.' },
  {
    path: '/operations',
    label: 'Fusion hub',
    desc: 'Security Operations Hub (under Fusion command in the sidebar, with AERTAGO and Pipeline as siblings). Playbooks, intel, IRP-style cases, assurance.',
  },
  {
    path: '/devguard',
    label: 'DevGuard',
    desc: 'Founder/engineer security scans for repo and website targets with prioritized hardening recommendations.',
  },
  {
    path: '/fusion/cyber-range',
    label: 'Cyber Range',
    desc: 'Controlled lab (Cyber Range): Red vs Blue status and orchestrator — under Fusion command in the sidebar.',
  },
  {
    path: '/autonomous-pipeline',
    label: 'Autonomous pipeline',
    desc: 'Orchestrated jobs with human approval gates for offensive steps — under Fusion in the sidebar.',
  },
  { path: '/critical-infrastructure', label: 'Infrastructure', desc: 'Critical assets and posture context.' },
  { path: '/risk', label: 'Risk', desc: 'Scenarios, findings, and risk-oriented views.' },
  { path: '/assets', label: 'Assets', desc: 'Inventory and asset-level detail.' },
  { path: '/network-scan', label: 'Network scan', desc: 'Scoped discovery when scanning is enabled in your environment.' },
  { path: '/compliance', label: 'Compliance', desc: 'Control and compliance alignment views.' },
  { path: '/verification-loop', label: 'Fix loop', desc: 'Verification and remediation readiness loop.' },
  { path: '/runs', label: 'Runs', desc: 'Executor and pipeline run history.' },
  { path: '/graph-findings', label: 'Graph findings', desc: 'Graph-backed findings when Neo4j is configured.' },
  { path: '/registry', label: 'Registry', desc: 'Attack paths and registry metadata.' },
  { path: '/remediation', label: 'Remediation', desc: 'Playbooks and remediation workflows.' },
  { path: '/forecasts', label: 'Forecasts', desc: 'Threat and defense forecasting inputs.' },
  { path: '/reports', label: 'Reports', desc: 'Exports and executive-ready reporting.' },
  { path: '/resimulation', label: 'Resimulation', desc: 'Replay and simulation tooling.' },
  { path: '/events', label: 'Events', desc: 'Live stream and WebSocket-backed activity.' },
  { path: '/platform', label: 'Platform (marketing)', desc: 'Public platform overview (outside the pilot shell).' },
];

export function NavigationHelpDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Where to go in AERTA" data-testid="nav-help-trigger">
          <HelpCircle className="h-5 w-5 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Navigate AERTA</DialogTitle>
          <DialogDescription>
            Quick map of pilot areas. Use the sidebar or jump-to search in the top bar. Open{' '}
            <strong className="text-foreground">V</strong> (bottom-right) for conversational navigation help when your
            operator enables <code className="text-xs">AERTA_LLM_*</code>. For grounded Q&amp;A on your latest risk report
            JSON, use the separate risk-report LLM assist — not the same as V.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-3 text-sm">
          {ROUTES.map((r) => (
            <li key={r.path} className="rounded-md border border-border/80 bg-secondary/20 px-3 py-2">
              <Link to={r.path} className="font-medium text-primary hover:underline">
                {r.label}
              </Link>
              <span className="block text-muted-foreground">{r.desc}</span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
